import { useState } from "react";
import { istYmdFromIso, weekdayDateLabel } from "./dateRange";

const COLORS = {
  ACTIVE: "#2b6df2",
  MANUAL_PAUSE: "#f59e0b",
  AUTO_PAUSE: "#7c3aed",
};

export default function SessionActivityChart({ sessions = [] }) {
  const [hover, setHover] = useState(null);
  const maxSeconds = Math.max(
    2 * 3600,
    ...sessions.map((session) =>
      (session.intervals || []).reduce((sum, item) => sum + (item.durationSeconds || 0), 0),
    ),
  );
  const maxHours = Math.ceil(maxSeconds / 3600);
  const ticks = [0, Math.round(maxHours / 2), maxHours];

  if (!sessions.length) {
    return <p className="pa-empty">No sessions in this period.</p>;
  }

  const onMove = (event, session) => {
    const wrap = event.currentTarget.closest(".pa-swim-wrap");
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    setHover({
      label: weekdayDateLabel(istYmdFromIso(session.startTime)),
      activeSeconds: sumByType(session, "ACTIVE"),
      autoPauseSeconds: sumByType(session, "AUTO_PAUSE"),
      manualPauseSeconds: sumByType(session, "MANUAL_PAUSE"),
      x,
      y,
      width: rect.width,
    });
  };

  return (
    <div className="pa-swim-wrap" onMouseLeave={() => setHover(null)}>
      <div className="pa-swim">
        {sessions.map((session) => {
          const total = (session.intervals || []).reduce(
            (sum, item) => sum + (item.durationSeconds || 0),
            0,
          );
          return (
            <div className="pa-swim__row" key={session.sessionId}>
              <div className="pa-swim__label">{weekdayDateLabel(istYmdFromIso(session.startTime))}</div>
              <div
                className="pa-swim__track"
                style={{ width: `${Math.max(8, (total / maxSeconds) * 100)}%` }}
                onMouseMove={(event) => onMove(event, session)}
              >
                {(session.intervals || []).map((item) => (
                  <span
                    key={item.intervalId}
                    className="pa-swim__seg"
                    style={{
                      width: `${total ? (item.durationSeconds / total) * 100 : 0}%`,
                      background: COLORS[item.intervalType] || "#cbd5e1",
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {hover ? (
        <div
          className="pa-tooltip"
          style={{
            left: hover.x,
            top: hover.y,
            transform: tooltipShift(hover.x, hover.width),
          }}
        >
          <strong>{hover.label}</strong>
          <p style={{ color: COLORS.ACTIVE }}>
            <i className="pa-dot" style={{ background: COLORS.ACTIVE }} />
            Active : {formatHourValue(hover.activeSeconds)}
          </p>
          <p style={{ color: COLORS.AUTO_PAUSE }}>
            <i className="pa-dot" style={{ background: COLORS.AUTO_PAUSE }} />
            Auto-paused : {formatHourValue(hover.autoPauseSeconds)}
          </p>
          <p style={{ color: COLORS.MANUAL_PAUSE }}>
            <i className="pa-dot" style={{ background: COLORS.MANUAL_PAUSE }} />
            Manual pause : {formatHourValue(hover.manualPauseSeconds)}
          </p>
        </div>
      ) : null}
      <div className="pa-axis">
        {ticks.map((tick) => (
          <span key={tick}>{tick}h</span>
        ))}
      </div>
      <div className="pa-legend">
        <span><i className="pa-dot" style={{ background: COLORS.ACTIVE }} /> Active</span>
        <span><i className="pa-dot" style={{ background: COLORS.MANUAL_PAUSE }} /> Manual pause</span>
        <span><i className="pa-dot" style={{ background: COLORS.AUTO_PAUSE }} /> Auto-paused</span>
      </div>
    </div>
  );
}

function sumByType(session, intervalType) {
  return (session.intervals || [])
    .filter((item) => item.intervalType === intervalType)
    .reduce((sum, item) => sum + (item.durationSeconds || 0), 0);
}

function formatHourValue(totalSeconds) {
  const hours = Math.round((Math.max(0, Number(totalSeconds) || 0) / 3600) * 10) / 10;
  return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
}

function tooltipShift(x, width) {
  if (x / width < 0.22) return "translate(8px, calc(-100% - 10px))";
  if (x / width > 0.78) return "translate(calc(-100% - 8px), calc(-100% - 10px))";
  return "translate(-50%, calc(-100% - 10px))";
}
